import { Doc } from '@dadohr/shared/domainTypes/document';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  UserImportDataSourceChartHop,
  UserImportDataSourceStatus,
  UserImportMapping,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import { Button, DialogActions, DialogContent } from '@material-ui/core';
import { useState } from 'react';
import shortid from 'shortid';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { Loader } from '../../../../../../components/Loader';
import { SomethingWentWrong } from '../../../../../../components/SomethingWentWrong';
import { usePromise } from '../../../../../../hooks/usePromise';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { setDoc } from '../../../../../../services/firebase';
import { now } from '../../../../../../services/time';
import { useUserFields } from '../../../../../../services/user';
import {
  getChartHopFields,
  useChartHopConfig
} from '../../../../../Org/pages/Integrations/integrations/charthop/service';
import { CustomImportMappingFormWithAutocomplete } from '../CustomImportMappingForm';
import { Img, ImportSourceListItem, IntegrationNotConfigured } from './common';
import { IMPORT_META } from './meta';

const META = IMPORT_META.CHARTHOP;

const DEFAULT_MAPPINGS: UserImportMapping[] = [
  {
    dadoKey: 'firstName',
    otherKey: 'name.first'
  },
  {
    dadoKey: 'preferredName',
    otherKey: 'name.pref'
  },
  {
    dadoKey: 'middleName',
    otherKey: 'name.middle'
  },
  {
    dadoKey: 'lastName',
    otherKey: 'name.last'
  },
  {
    dadoKey: 'email',
    otherKey: 'contact.workEmail'
  },
  {
    dadoKey: 'personalEmail',
    otherKey: 'contact.homeEmail'
  },
  {
    dadoKey: 'department',
    otherKey: 'department.name'
  },
  {
    dadoKey: 'location',
    otherKey: 'location.name'
  },
  {
    dadoKey: 'role',
    otherKey: 'title'
  },
  {
    dadoKey: 'team',
    otherKey: 'team.name'
  },
  {
    dadoKey: 'managerId',
    otherKey: 'manager.contact.workEmail'
  },
  {
    dadoKey: 'hireDate',
    otherKey: 'startDate'
  }
];

export const ChartHopSetupFlow = ({
  orgId,
  onClose,
  customFields
}: {
  orgId: string;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { id: userId } = useCurrentUser();
  const [config, loading, error] = useChartHopConfig(orgId, userId);
  const isSetup = !!(config?.data.orgSlug && config?.data.apiToken);
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customFields);

  const [mappings, setMappings] = useState<UserImportMapping[]>(
    DEFAULT_MAPPINGS
  );

  const [chartHopFields, loadingFields, errorFields] = usePromise(async () => {
    const result = await getChartHopFields(orgId);
    return result;
  });

  if (loading || loadingFields) {
    return (
      <DialogContent>
        <Loader height={250} />
      </DialogContent>
    );
  }
  if (error || errorFields) {
    return <SomethingWentWrong height={150} />;
  }
  if (!config) {
    return null;
  }

  if (!isSetup) {
    return (
      <IntegrationNotConfigured integration={META.name} onClose={onClose} />
    );
  }

  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={META.logoUrl} />
        </FormRow>

        {chartHopFields && (
          <CustomImportMappingFormWithAutocomplete
            additionalCompositeFields={['$FULL_NAME$']}
            userFields={userFields}
            integrationName={META.name}
            name={t('Default Field Mapping')}
            mappings={mappings}
            onChange={(mappings) => setMappings(mappings)}
            fields={chartHopFields}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={async () => {
            const nextSource: Doc<UserImportDataSourceChartHop> = {
              id: shortid(),
              collection: FS.userImportDataSources,
              data: {
                orgId: orgId,
                type: UserImportSourceType.CHARTHOP,
                status: UserImportDataSourceStatus.PENDING,
                detailedStatus: '',
                lastUpdate: null,
                createdBy: userId,
                createdAt: now(),
                customMappings: mappings.filter((m) => !!m.otherKey)
              }
            };
            await setDoc(nextSource);
            onClose();
          }}
        >
          {t('Add source')}
        </Button>
      </DialogActions>
    </>
  );
};

export const ChartHopExistingSetup = ({
  d,
  onClose,
  customFields
}: {
  d: Doc<UserImportDataSourceChartHop>;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  // this is currently not in use anymore and needs to be cleaned up
  return null;
};

export const ImportSourceRowChartHop = ({
  d
}: {
  d: Doc<UserImportDataSourceChartHop>;
}) => {
  return (
    <ImportSourceListItem
      enabled={d.data.status === UserImportDataSourceStatus.OK}
      name={META.name}
      logoUrl={META.smallLogoUrl}
    />
  );
};
